import React from "react";
import filter from "../../filter";
import {INVESTOR_LIST, INVESTOR_LIST_COUNT} from "../../consts/queries";
import Dropdown from "../Dropdown/Dropdown";
import PropTypes from "prop-types";
import { executeBackendQuery } from "../../requests/executeBackendQuery";
import { getData } from "../../requests/CRUD";
import { formatName } from "../../utils";
import { INVESTORS } from "../../consts/tables";
import DeceasedPill from "../DeceasedPill/DeceasedPill";

const cloneDeep = require("lodash.clonedeep");
const limit = 10;
const debounceMs = 250;

class SelectInvestor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            currentOffset: 0,
            hasMore: true,
            isLoading: false,
            searchTerm: "",
            inputValue: "",
            selectedOptions: [],
        };

        this.debounceTimer = null;
        this.activeRequestId = 0;

        this.fetchPage = this.fetchPage.bind(this);
        this.startSearch = this.startSearch.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectedOptionsChange = this.handleSelectedOptionsChange.bind(this);
        this.syncSelectedOptionsFromValue = this.syncSelectedOptionsFromValue.bind(this);
        this.ensureSelectedOption = this.ensureSelectedOption.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
        this.isMultiple = this.isMultiple.bind(this);
        this.getOptionAddress = this.getOptionAddress.bind(this);
        this.renderOption = this.renderOption.bind(this);
    }

    componentDidMount() {
        this.startSearch("");
        this.ensureSelectedOption(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.newInvestor !== prevProps.newInvestor && this.props.newInvestor) {
            const items = cloneDeep(this.state.items);
            items.push(this.props.newInvestor);
            this.setState({items});
        }

        if (prevProps.value !== this.props.value) {
            this.ensureSelectedOption(this.props.value);
        }

        if (prevProps.value !== this.props.value || prevState.items !== this.state.items) {
            this.syncSelectedOptionsFromValue();
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.activeRequestId++;
    }

    fetchPage({offset, searchTerm, replace}) {
        const requestId = ++this.activeRequestId;
        const trimmedTerm = (searchTerm || "").trim();
        const searchPattern = trimmedTerm ? `%${trimmedTerm}%` : "";

        this.setState({isLoading: true});

        if (!trimmedTerm) {
            const options = filter({NAME: ""}, "investors");
            options.param.limit = limit;
            options.param.offset = offset;
            options.param.NAME = "";

            return Promise.all([
                executeBackendQuery({queryName: INVESTOR_LIST, options}),
                executeBackendQuery({queryName: INVESTOR_LIST_COUNT, options}),
            ])
                .then((responses) => {
                    if (requestId !== this.activeRequestId) return;
                    const listRes = responses && responses[0] ? responses[0] : null;
                    const countRes = responses && responses[1] ? responses[1] : null;
                    if (listRes && listRes.success && Array.isArray(listRes.data)) {
                        const newItems = listRes.data.filter(item => item && item.NAME !== "" && (item.DELETED === null || item.DELETED === undefined || item.DELETED === '' || item.DELETED === '0' || item.DELETED === 0));
                        const totalCount = countRes && countRes.success && countRes.data && countRes.data[0]
                            ? Number(countRes.data[0].TOTAL_COUNT)
                            : null;
                        this.setState((prev) => ({
                            items: replace ? newItems : [...prev.items, ...newItems],
                            currentOffset: offset + newItems.length,
                            hasMore: totalCount === null ? (newItems.length === limit) : (offset + newItems.length < totalCount),
                            isLoading: false,
                            searchTerm,
                        }));
                    } else {
                        this.setState({isLoading: false, hasMore: false});
                    }
                })
                .catch((err) => {
                    if (requestId !== this.activeRequestId) return;
                    console.error("Error fetching investors:", err);
                    this.setState({isLoading: false});
                });
        }

        // One server-side search over name, firm name and both parties'
        // addresses (CW9). It replaces the two parallel NAME/FIRM_NAME requests
        // this used to merge client-side: an address only matches when the
        // whole typed phrase is weighed at once, so "14755 Preston" cannot be
        // split across two queries and stitched back together here.
        const fetchLimit = 15;
        const optsSearch = filter({SEARCH: trimmedTerm}, "investors");
        optsSearch.param.limit = fetchLimit;
        optsSearch.param.offset = 0;
        optsSearch.param.SEARCH = searchPattern;

        return executeBackendQuery({queryName: INVESTOR_LIST, options: optsSearch})
            .then((res) => {
                if (requestId !== this.activeRequestId) return;

                const seen = new Map();
                const merged = [];
                if (res && res.success && Array.isArray(res.data)) {
                    for (const item of res.data) {
                        if (item && item.NAME !== "" && (item.DELETED === null || item.DELETED === undefined || item.DELETED === '' || item.DELETED === '0' || item.DELETED === 0) && !seen.has(String(item.ARCIMEDES_ROW_ID))) {
                            seen.set(String(item.ARCIMEDES_ROW_ID), true);
                            merged.push(item);
                        }
                    }
                }

                const newItems = merged;
                this.setState((prev) => ({
                    items: newItems,
                    currentOffset: newItems.length,
                    hasMore: false,
                    isLoading: false,
                    searchTerm,
                }));
            })
            .catch((err) => {
                if (requestId !== this.activeRequestId) return;
                console.error("Error fetching investors:", err);
                this.setState({isLoading: false});
            });
    }

    startSearch(searchTerm) {
        // Reset pagination for new term
        this.setState({
            items: [],
            currentOffset: 0,
            hasMore: true,
            searchTerm,
        });
        return this.fetchPage({offset: 0, searchTerm, replace: true});
    }

    handleInputChange(event, newValue, reason) {
        // Material-UI triggers onInputChange for multiple reasons.
        // We ONLY update inputValue on actual user typing, otherwise MUI can "reset" it to empty
        // when options/value change (which feels like the input keeps clearing).
        if (reason === "reset") return;
        if (reason === "clear") {
            this.setState({inputValue: ""});
            this.startSearch("");
            return;
        }

        const next = (newValue || "").trimStart();
        this.setState({inputValue: next});

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.startSearch(next);
        }, debounceMs);
    }

    handleLoadMore() {
        if (!this.state.isLoading && this.state.hasMore) {
            this.fetchPage({
                offset: this.state.currentOffset,
                searchTerm: this.state.searchTerm,
                replace: false,
            });
        }
    }

    getOptionLabel(option) {
        if (!option || typeof option !== 'object') return '';
        return formatName(option) || '';
    }

    /**
     * Chips carry the selection here; elsewhere (the listing filters, bulk
     * edit) the box itself is the only place it shows, so it keeps the name.
     */
    isMultiple() {
        return this.props.dropdownType === 'multiple';
    }

    /**
     * The investor's own mailing address, or their firm's when they have none
     * of their own — whichever one a search by address would have matched.
     */
    getOptionAddress(option) {
        if (!option || typeof option !== 'object') return '';

        const pick = (own, firm) => {
            const value = option.ADDRESS ? option[own] : option[firm];
            return value ? String(value).trim() : '';
        };

        const street = pick('ADDRESS', 'FIRM_ADDRESS');
        const city = pick('CITY', 'FIRM_CITY');
        const state = pick('STATE', 'FIRM_STATE');
        const zip = pick('ZIP', 'FIRM_ZIP');

        const cityState = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        return [street, cityState].filter(Boolean).join(', ');
    }

    /**
     * Two lines per option: who they are, and where they take mail. The second
     * line is the whole point of searching by address — it is what tells the
     * researcher that the row already in the table is the owner they were about
     * to add again.
     */
    renderOption(option) {
        // Dropdown can put a rendered element in the list (the "add new" row).
        // Leave anything that is not an investor record as it came.
        if (React.isValidElement(option)) return option;
        if (!option || typeof option !== 'object') return option || '';

        const name = this.getOptionLabel(option);
        const firmName = option && option.FIRM_NAME && String(option.FIRM_NAME).trim();
        const detail = [
            this.getOptionAddress(option),
            option.SECOND_CONTACT_NAME && `2nd Contact: ${option.SECOND_CONTACT_NAME}`,
        ].filter(Boolean).join(' · ');

        return (
            <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0}}>
                <span>
                    {firmName ? `${name || ''} - ${firmName}` : (name || '')}
                    <DeceasedPill investor={option} />
                </span>
                {detail && (
                    <span style={{fontSize: '0.8em', opacity: 0.65, fontWeight: 'normal'}}>{detail}</span>
                )}
            </div>
        );
    }

    ensureSelectedOption(value) {
        const id = this.extractId(value);
        if (!id) return;

        const alreadyThere = this.state.items.find(item => this.extractId(item) === id);
        if (alreadyThere) return;

        getData({
            tableCode: INVESTORS,
            options: {
                limit: { count: 1, offset: 0 },
                column: ["ARCIMEDES_ROW_ID", "NAME", "EMAIL", "PHONE"],
                filter: [{ code: "ARCIMEDES_ROW_ID", text: id, type: "equal" }],
                filterType: "and",
            },
        })
            .then((res) => {
                if (res && res.success && Array.isArray(res.data) && res.data.length) {
                    this.setState((prev) => ({
                        items: [res.data[0], ...prev.items],
                    }), this.syncSelectedOptionsFromValue);
                }
            })
            .catch(() => {});
    }

    extractId(option) {
        if (!option) return null;
        if (typeof option === 'object') {
            if (option.ARCIMEDES_ROW_ID !== undefined) return String(option.ARCIMEDES_ROW_ID);
            if (option.value !== undefined) return String(option.value);
        }
        return option !== undefined && option !== null ? String(option) : null;
    }

    getSelectedIdsFromValue() {
        const {value} = this.props;
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value.map((v) => String(this.extractId(v))).filter(Boolean);
        if (typeof value === 'object') {
            const id = this.extractId(value);
            return id ? [id] : [];
        }
        return String(value).split(',').map(v => v.trim()).filter(Boolean);
    }

    syncSelectedOptionsFromValue() {
        const selectedIds = this.getSelectedIdsFromValue();
        if (!selectedIds.length) {
            if (this.state.selectedOptions.length) {
                this.setState({selectedOptions: []});
            }
            return;
        }

        const existingMap = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) existingMap.set(id, opt);
        });

        selectedIds.forEach(id => {
            if (existingMap.has(id)) return;
            const found = this.state.items.find(opt => this.extractId(opt) === id);
            if (found) {
                existingMap.set(id, found);
            }
        });

        const next = Array.from(existingMap.values());
        // Avoid unnecessary state updates
        if (next.length === this.state.selectedOptions.length) {
            let isSame = true;
            for (let i = 0; i < next.length; i++) {
                if (this.extractId(next[i]) !== this.extractId(this.state.selectedOptions[i])) {
                    isSame = false;
                    break;
                }
            }
            if (isSame) return;
        }

        const nextState = { selectedOptions: next };
        if (!this.isMultiple() && !this.state.searchTerm && next.length === 1) {
            const label = this.getOptionLabel(next[0]);
            if (label && this.state.inputValue !== label) {
                nextState.inputValue = label;
            }
        }
        this.setState(nextState);
    }

    handleSelectedOptionsChange(options) {
        const nextOptions = Array.isArray(options) ? options.filter(Boolean) : (options ? [options] : []);
        const map = new Map();
        nextOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        const selectedOptions = Array.from(map.values());
        const nextState = { selectedOptions };

        // A multi-select shows its selection as chips, so the text left in the
        // box after a pick is only the search that found it — and standing
        // beside a chip of the same name it reads as the investor being added
        // twice. Clearing it also leaves the box ready for the next name,
        // which is the point of a field that takes several.
        if (this.isMultiple()) {
            this.setState({...nextState, inputValue: "", searchTerm: ""}, () => {
                // Back to the unfiltered list, so opening the box again offers
                // everyone rather than the last search's leftovers.
                this.startSearch("");
            });

            return;
        }

        if (selectedOptions.length === 1) {
            const label = this.getOptionLabel(selectedOptions[0]);
            if (label) {
                nextState.inputValue = label;
                nextState.searchTerm = "";
            }
        }
        if (selectedOptions.length === 0) {
            nextState.inputValue = "";
            nextState.searchTerm = "";
        }

        this.setState(nextState);
    }

    render() {
        const {items, hasMore, isLoading, inputValue} = this.state;
        const {
            theme,
            value,
            valueKey,
            label,
            disabled,
            data,
            withAddNew,
            handleParentChange,
            addNewItem,
            deleted,
            viewMode,
            excludeInvestorIds,
            ...rest
        } = this.props;

        // Filter out already attached investors
        let filteredItems = items;
        if (excludeInvestorIds && excludeInvestorIds.length > 0) {
            filteredItems = items.filter(item => 
                !excludeInvestorIds.includes(String(item.ARCIMEDES_ROW_ID))
            );
        }

        // Ensure currently selected options stay in the list even when search results change
        const mergedOptionsMap = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) mergedOptionsMap.set(id, opt);
        });
        filteredItems.forEach(opt => {
            const id = this.extractId(opt);
            if (!id) return;
            if (excludeInvestorIds && excludeInvestorIds.includes(String(id)) && !mergedOptionsMap.has(String(id))) {
                return;
            }
            if (!mergedOptionsMap.has(String(id))) mergedOptionsMap.set(String(id), opt);
        });
        const mergedOptions = Array.from(mergedOptionsMap.values());

        return (
            <div>
                <Dropdown
                    theme={theme}
                    optionsType="investor"
                    optionKey="ARCIMEDES_ROW_ID"
                    options={mergedOptions}
                    value={value}
                    valueKey={valueKey}
                    label={label}
                    disabled={disabled}
                    data={data}
                    withAddNew={withAddNew}
                    handleParentChange={handleParentChange}
                    addNewItem={addNewItem}
                    deleted={deleted}
                    viewMode={viewMode}
                    disableClientFilter={true}
                    renderOption={this.renderOption}
                    onInputChange={this.handleInputChange}
                    inputValue={inputValue}
                    loading={isLoading}
                    loadingText="Searching..."
                    noOptionsText={inputValue ? "No matching investors" : "Type to search investors"}
                    onSelectedOptionsChange={this.handleSelectedOptionsChange}
                    ListboxProps={{
                        onScroll: (e) => {
                            const { target } = e;
                            // Load more when user scrolls near the bottom (80% of scroll height)
                            if (target.scrollTop + target.clientHeight >= target.scrollHeight * 0.8) {
                                if (hasMore && !isLoading) {
                                    this.handleLoadMore();
                                }
                            }
                        }
                    }}
                    {...rest}
                />
            </div>
        );
    }
}

SelectInvestor.propTypes = {
    theme: PropTypes.oneOf(["default", "white", "dark"]),
    dropdownType: PropTypes.oneOf(["single", "multiple"]),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    withAddNew: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    viewMode: PropTypes.bool,
    addNewItem: PropTypes.func,
    deleted: PropTypes.bool,
    isFromProperty: PropTypes.bool,
    newInvestor: PropTypes.object,
    excludeInvestorIds: PropTypes.array,
};

export default SelectInvestor;
